import React, { useState, useEffect } from 'react';
import {
  buscarConferenteById,
  atualizarConferente
} from '../../services/conferente';
import { listarClientes } from '../../services/clientes';
import UserService from '../../services/users';
import ModalPortal from '../../components/Common/ModalPortal';

interface ConferenteFormModalProps {
  conferenteId: number;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

const TIPO_OPTIONS = [
  { value: 'funcionario', label: 'Funcionário' },
  { value: 'freelancer', label: 'Freelancer' },
  { value: 'cooperado', label: 'Cooperado' }
];

const ConferenteFormModal: React.FC<ConferenteFormModalProps> = ({ conferenteId, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dados do formulário
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [telefone, setTelefone] = useState('');
  const [tipoConferente, setTipoConferente] = useState('freelancer');
  const [filialId, setFilialId] = useState<number | null>(null);
  const [selectedClienteIds, setSelectedClienteIds] = useState<number[]>([]);
  const [ativo, setAtivo] = useState(true);
  const [status, setStatus] = useState('');

  // Listas para selects
  const [filiais, setFiliais] = useState<{ id: number; razao_social: string; nome_fantasia?: string; cnpj: string }[]>([]);
  const [clientesDisponiveis, setClientesDisponiveis] = useState<{ id: number; razao_social: string; cnpj?: string }[]>([]);

  // Carregar dados do conferente + filiais + clientes
  useEffect(() => {
    const carregar = async () => {
      try {
        setLoading(true);
        const [confResult, filiaisResult, clientesResult] = await Promise.all([
          buscarConferenteById(conferenteId),
          UserService.getFiliais().catch(() => ({ success: false, filiais: [] as any[] })),
          listarClientes({ limit: 200, ativo: 'true' }).catch(() => ({ success: false, clientes: [] as any[] }))
        ]);

        const conf = confResult.conferente;
        setNome(conf.nome);
        setCpf(conf.cpf);
        setTelefone(conf.telefone || '');
        setTipoConferente(conf.tipo_conferente || 'freelancer');
        setFilialId(conf.filial_id || null);
        setAtivo(conf.ativo);
        setStatus(conf.status);

        // Clientes vinculados
        if (confResult.clientes && confResult.clientes.length > 0) {
          setSelectedClienteIds(confResult.clientes.map(c => c.id));
        }

        // Filiais
        if (filiaisResult.success && filiaisResult.filiais) {
          setFiliais(filiaisResult.filiais);
        }

        // Clientes disponíveis
        if (clientesResult.success && clientesResult.clientes) {
          setClientesDisponiveis(clientesResult.clientes.map(c => ({
            id: c.id, razao_social: c.razao_social, cnpj: c.cnpj
          })));
        }
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar dados do conferente');
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, [conferenteId]);

  const handleClienteToggle = (clienteId: number) => {
    setSelectedClienteIds(prev =>
      prev.includes(clienteId)
        ? prev.filter(id => id !== clienteId)
        : [...prev, clienteId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await atualizarConferente(conferenteId, {
        nome: nome.trim(),
        telefone: telefone.trim() || undefined,
        tipo_conferente: tipoConferente,
        filial_id: filialId || undefined,
        ativo,
        cliente_ids: selectedClienteIds
      });
      onSuccess('Conferente atualizado com sucesso');
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar conferente');
    } finally {
      setSaving(false);
    }
  };

  const formatCpf = (val: string) => {
    if (!val || val.length !== 11) return val;
    return `${val.slice(0, 3)}.${val.slice(3, 6)}.${val.slice(6, 9)}-${val.slice(9)}`;
  };

  const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
    pendente: { label: 'Pendente', color: '#d97706', bg: '#fef3c7' },
    aprovado: { label: 'Aprovado', color: '#16a34a', bg: '#dcfce7' },
    reprovado: { label: 'Reprovado', color: '#dc2626', bg: '#fee2e2' }
  };

  return (
    <ModalPortal isOpen={true}>
      <div className="modal-overlay" onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          background: '#fff', borderRadius: 12, width: '100%', maxWidth: 600,
          maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          {/* Header */}
          <div style={{
            padding: '20px 24px', borderBottom: '1px solid #e2e8f0',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Editar Conferente</h2>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer',
              color: '#64748b', lineHeight: 1
            }}>&times;</button>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Carregando...</div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '20px 24px' }}>
                {error && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 8, marginBottom: 16,
                    background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', fontSize: '0.85rem'
                  }}>{error}</div>
                )}

                {/* Status badge */}
                {status && (
                  <div style={{ marginBottom: 16 }}>
                    <span style={{
                      padding: '4px 12px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 600,
                      color: STATUS_LABELS[status]?.color || '#64748b',
                      background: STATUS_LABELS[status]?.bg || '#f1f5f9'
                    }}>
                      Status: {STATUS_LABELS[status]?.label || status}
                    </span>
                  </div>
                )}

                {/* Nome */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>
                    Nome <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={nome}
                    onChange={e => setNome(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                      borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box'
                    }}
                    required
                  />
                </div>

                {/* CPF (somente leitura) */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>CPF</label>
                  <input
                    type="text"
                    value={formatCpf(cpf)}
                    disabled
                    style={{
                      width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0',
                      borderRadius: 8, fontSize: '0.9rem', background: '#f8fafc', color: '#64748b',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Telefone + Tipo */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>Telefone</label>
                    <input
                      type="tel"
                      value={telefone}
                      onChange={e => setTelefone(e.target.value)}
                      placeholder="(00) 00000-0000"
                      style={{
                        width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                        borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>Tipo</label>
                    <select
                      value={tipoConferente}
                      onChange={e => setTipoConferente(e.target.value)}
                      style={{
                        width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                        borderRadius: 8, fontSize: '0.9rem', background: '#fff', boxSizing: 'border-box'
                      }}
                    >
                      {TIPO_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Ativo */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    padding: '10px 12px', borderRadius: 8,
                    background: ativo ? '#dcfce7' : '#fee2e2',
                    border: `1px solid ${ativo ? '#86efac' : '#fca5a5'}`
                  }}>
                    <input
                      type="checkbox"
                      checked={ativo}
                      onChange={e => setAtivo(e.target.checked)}
                      style={{ width: 16, height: 16 }}
                    />
                    <span style={{ fontWeight: 500, fontSize: '0.9rem', color: ativo ? '#16a34a' : '#dc2626' }}>
                      {ativo ? 'Conferente ativo' : 'Conferente inativo'}
                    </span>
                  </label>
                </div>

                {/* Filial */}
                <div style={{
                  marginBottom: 16, padding: 16, borderRadius: 8,
                  border: '1px solid #e2e8f0', background: '#f8fafc'
                }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: '0.95rem', fontWeight: 600, color: '#374151' }}>
                    Filial e Clientes
                  </h3>

                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>Filial</label>
                    {filiais.length === 0 ? (
                      <div style={{ padding: '8px 0', color: '#7f8c8d', fontSize: '0.85rem' }}>
                        Nenhuma filial cadastrada
                      </div>
                    ) : (
                      <select
                        value={filialId || ''}
                        onChange={e => setFilialId(e.target.value ? parseInt(e.target.value) : null)}
                        style={{
                          width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                          borderRadius: 8, fontSize: '0.9rem', background: '#fff', boxSizing: 'border-box'
                        }}
                      >
                        <option value="">Selecione uma filial</option>
                        {filiais.map(f => (
                          <option key={f.id} value={f.id}>
                            {f.nome_fantasia || f.razao_social} ({f.cnpj})
                          </option>
                        ))}
                      </select>
                    )}
                    <small style={{ display: 'block', marginTop: 4, color: '#7f8c8d', fontSize: '0.75rem' }}>
                      Filial onde este conferente está alocado
                    </small>
                  </div>

                  {/* Clientes vinculados */}
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', fontWeight: 500, color: '#374151' }}>
                      Clientes Vinculados
                    </label>
                    {clientesDisponiveis.length === 0 ? (
                      <div style={{ padding: '8px 0', color: '#7f8c8d', fontSize: '0.85rem' }}>
                        Nenhum cliente cadastrado
                      </div>
                    ) : (
                      <div style={{
                        maxHeight: 200, overflowY: 'auto', border: '1px solid #ddd',
                        borderRadius: 8, padding: 8, background: '#fff'
                      }}>
                        {clientesDisponiveis.map(cliente => (
                          <label
                            key={cliente.id}
                            style={{
                              display: 'flex', alignItems: 'center', padding: '6px 8px',
                              borderRadius: 6, cursor: 'pointer',
                              backgroundColor: selectedClienteIds.includes(cliente.id) ? '#e8f5e9' : 'transparent',
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedClienteIds.includes(cliente.id)}
                              onChange={() => handleClienteToggle(cliente.id)}
                              style={{ marginRight: 8 }}
                            />
                            <span style={{ flex: 1 }}>
                              <strong style={{ fontSize: '0.85rem' }}>{cliente.razao_social}</strong>
                              {cliente.cnpj && (
                                <small style={{ display: 'block', color: '#7f8c8d', fontSize: '0.72rem' }}>
                                  CNPJ: {cliente.cnpj}
                                </small>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                    <small style={{ display: 'block', marginTop: 4, color: '#7f8c8d', fontSize: '0.75rem' }}>
                      {selectedClienteIds.length === 0
                        ? 'Selecione os clientes. Se nenhum for selecionado, todos serão exibidos.'
                        : selectedClienteIds.length === 1
                          ? '1 cliente selecionado - será selecionado automaticamente na conferência'
                          : `${selectedClienteIds.length} clientes selecionados`
                      }
                    </small>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{
                padding: '16px 24px', borderTop: '1px solid #e2e8f0',
                display: 'flex', justifyContent: 'flex-end', gap: 8
              }}>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db',
                    background: '#fff', color: '#374151', fontSize: '0.9rem', cursor: 'pointer', fontWeight: 500
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: '#16a34a', color: '#fff', fontSize: '0.9rem', cursor: 'pointer',
                    fontWeight: 500, opacity: saving ? 0.6 : 1
                  }}
                >
                  {saving ? 'Salvando...' : 'Salvar Alteracoes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default ConferenteFormModal;
